import * as React from "react";
import { clsx } from "clsx";
import { CircleAlert, Equal, TriangleAlert } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip } from "@/components/ui/tooltip";
import {
  REVIEW_VERDICT_LABEL,
  REVIEW_VERDICT_VARIANT,
  SnapshotReviewVerdict,
} from "@/modules/snapshots/businessReports.types";

const VERDICT_ICON: Record<SnapshotReviewVerdict, React.ComponentType<{ className?: string }>> = {
  material_difference: CircleAlert,
  inconclusive: TriangleAlert,
  no_material_difference: Equal,
};

interface Props {
  verdict: SnapshotReviewVerdict;
  shortExplanation?: string | null;
  compact?: boolean;
  className?: string;
}

export const ReviewVerdictBadge: React.FC<Props> = ({ verdict, shortExplanation, compact = false, className }) => {
  const label = REVIEW_VERDICT_LABEL[verdict];
  const Icon = VERDICT_ICON[verdict];

  if (compact) {
    const tooltip = shortExplanation ? `${label} — ${shortExplanation}` : label;
    return (
      <Tooltip text={tooltip} delayDuration={50}>
        <span aria-label={`Review verdict: ${label}`} className={clsx("inline-flex flex-shrink-0", className)}>
          <Badge variant={REVIEW_VERDICT_VARIANT[verdict]} className="h-4 w-4 items-center justify-center p-0.5">
            <Icon className="h-3 w-3" />
          </Badge>
        </span>
      </Tooltip>
    );
  }

  return (
    <Badge
      variant={REVIEW_VERDICT_VARIANT[verdict]}
      aria-label={`Review verdict: ${label}`}
      className={clsx("whitespace-nowrap", className)}
    >
      <Icon className="mr-1 h-3 w-3" />
      {label}
    </Badge>
  );
};
